import React, { useState, useContext, useEffect } from 'react';
import Header from '../components/Header';
import Tooltip from '../components/Tooltip';
import Footer from '../components/Footer';
import { AuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { changePassword, getUserSettings, updateUserSettings, updateUserProfile, getSites, updateSite } from '../utils/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil, faCircleQuestion } from '@fortawesome/free-solid-svg-icons';
import { ROLE_SUPERADMIN, ROLE_FACILITY_MANAGER, ROLE_PLANT_CARETAKER, ROLE_NAMES } from '../utils/roles';
import { SITE_COUNTRIES } from '../constants/locations';
import AddSite from '../components/AddSite';

const WEEKDAYS = [
        { label: 'Mon', bit: 1 },
        { label: 'Tue', bit: 2 },
        { label: 'Wed', bit: 4 },
        { label: 'Thu', bit: 8 },
        { label: 'Fri', bit: 16 },
];

const SettingsPage = () => {
        const { user, updateUser } = useContext(AuthContext);
        const { showToast } = useToast();

        const [currentPassword, setCurrentPassword] = useState('');
        const [newPassword, setNewPassword] = useState('');
        const [confirmPassword, setConfirmPassword] = useState('');
        const [isSubmitting, setIsSubmitting] = useState(false);
        const [error, setError] = useState(null);

        // Profile editing state
        const [isEditingProfile, setIsEditingProfile] = useState(false);
        const [firstName, setFirstName] = useState('');
        const [lastName, setLastName] = useState('');
        const [profileSaving, setProfileSaving] = useState(false);

        // Email notification settings state
        const [settings, setSettings] = useState({
                id: 0,
                user_id: 0,
                out_of_office: false,
                email_weekdays: 21,
                monthly_report: false,
                subscribed_sites: [],
        });
        const [sites, setSites] = useState([]);
        const [settingsLoading, setSettingsLoading] = useState(true);
        const [settingsSaving, setSettingsSaving] = useState(false);

        // Single-site customers never see the site level on the dashboard, so this
        // is where they name their location and set the country that tunes plant
        // targets. Multi-site customers get the same editor, once per row.
        const singleSite = sites.length === 1 ? sites[0] : null;
        // The id of the site being edited, or null — an id rather than a flag so
        // multi-site rows reuse the single-site editor, and so only one row can be
        // open at a time.
        const [editingSiteId, setEditingSiteId] = useState(null);
        const [locationName, setLocationName] = useState('');
        const [locationCountry, setLocationCountry] = useState('');
        const [locationSaving, setLocationSaving] = useState(false);
        const isEditingSingleSite = !!singleSite && editingSiteId === singleSite.id;

        // Initialize profile fields when user data is available
        useEffect(() => {
                if (user) {
                        setFirstName(user.firstName || '');
                        setLastName(user.lastName || '');
                }
        }, [user]);

        const handleEditProfile = () => {
                setFirstName(user?.firstName || '');
                setLastName(user?.lastName || '');
                setIsEditingProfile(true);
        };

        const handleCancelEditProfile = () => {
                setIsEditingProfile(false);
                setFirstName(user?.firstName || '');
                setLastName(user?.lastName || '');
        };

        const handleSaveProfile = async () => {
                setProfileSaving(true);
                try {
                        const response = await updateUserProfile({
                                first_name: firstName,
                                last_name: lastName
                        });
                        if (!response.ok) {
                                throw new Error('Failed to update profile');
                        }
                        updateUser({ firstName, lastName });
                        setIsEditingProfile(false);
                        showToast('success', 'Profile updated successfully');
                } catch (err) {
                        console.error('Failed to save profile:', err);
                        showToast('error', 'Failed to update profile');
                } finally {
                        setProfileSaving(false);
                }
        };

        const handleChangePassword = async (e) => {
                e.preventDefault();
                setError(null);

                if (!currentPassword || !newPassword || !confirmPassword) {
                        setError('All fields are required');
                        return;
                }

                if (newPassword !== confirmPassword) {
                        setError('New passwords do not match');
                        return;
                }

                if (newPassword.length < 8) {
                        setError('Password must be at least 8 characters');
                        return;
                }

                setIsSubmitting(true);

                try {
                        const response = await changePassword({
                                current_password: currentPassword,
                                new_password: newPassword
                        });

                        if (!response.ok) {
                                const data = await response.json();
                                if (data.error.details.length > 0) {
                                        // TODO: Show validation errors for each field
                                        data.error.details.forEach(detail => {
                                                throw new Error(detail.message);
                                        });
                                }
                                throw new Error(data.error.message || 'Failed to change password');
                        }

                        setCurrentPassword('');
                        setNewPassword('');
                        setConfirmPassword('');
                        showToast('success', 'Password changed successfully');
                } catch (err) {
                        setError(err.message);
                        showToast('error', 'Failed to change password');
                } finally {
                        setIsSubmitting(false);
                }
        };

        useEffect(() => {
                const fetchSettings = async () => {
                        try {
                                const response = await getUserSettings();
                                if (response.ok) {
                                        const resp = await response.json();
                                        setSettings({
                                                id: resp.data.id,
                                                user_id: resp.data.user_id,
                                                out_of_office: resp.data.out_of_office,
                                                email_weekdays: resp.data.email_weekdays,
                                                monthly_report: resp.data.monthly_report,
                                                subscribed_sites: resp.data.subscribed_sites.map(s => s.site_id),
                                        });
                                }
                        } catch (err) {
                                console.error('Failed to fetch settings:', err);
                                showToast('error', 'Failed to load settings');
                        } finally {
                                setSettingsLoading(false);
                        }
                };

                const fetchSites = async () => {
                        try {
                                const response = await getSites();
                                if (response.ok) {
                                        const resp = await response.json();
                                        setSites(resp.data || []);
                                }
                        } catch (err) {
                                console.error('Failed to fetch sites:', err);
                                showToast('error', 'Failed to load sites');
                        }
                };

                fetchSettings();
                fetchSites();
        }, [showToast]);

        // Seeding the fields here rather than in an effect keeps them from being
        // reset under the user while they type, and means cancelling is just
        // dropping the open row.
        const handleStartEditLocation = (site) => {
                setEditingSiteId(site.id);
                setLocationName(site.name);
                setLocationCountry(site.country || '');
        };

        const handleCancelEditLocation = () => {
                setEditingSiteId(null);
        };

        const handleSaveLocation = async () => {
                if (editingSiteId === null) return;
                if (!locationName.trim()) {
                        showToast('error', 'Location name is required');
                        return;
                }
                setLocationSaving(true);
                try {
                        const response = await updateSite(editingSiteId, {
                                name: locationName.trim(),
                                country: locationCountry,
                        });
                        if (!response.ok) {
                                throw new Error('Failed to update location');
                        }
                        setSites(prev => prev.map(s => (
                                s.id === editingSiteId
                                        ? { ...s, name: locationName.trim(), country: locationCountry }
                                        : s
                        )));
                        setEditingSiteId(null);
                        showToast('success', 'Location updated successfully');
                } catch (err) {
                        console.error('Failed to save location:', err);
                        showToast('error', 'Failed to update location');
                } finally {
                        setLocationSaving(false);
                }
        };

        // Adding a second location switches the customer to multi-site: the site
        // level appears on the dashboard and this card switches to a row per site.
        const handleLocationAdded = (newSite) => {
                setSites(prev => [...prev, newSite]);
                setEditingSiteId(null);
        };

        // Shared by the single-site editor and each multi-site row, so the two
        // never drift apart.
        const locationEditActions = (
                <div className="flex gap-2">
                        <button
                                onClick={handleCancelEditLocation}
                                data-testid="location-cancel"
                                disabled={locationSaving}
                                className="px-3 py-1 text-sm border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
                        >
                                Cancel
                        </button>
                        <button
                                onClick={handleSaveLocation}
                                data-testid="location-save"
                                disabled={locationSaving}
                                className="px-3 py-1 text-sm bg-brand text-white rounded-lg hover:bg-brand/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                                {locationSaving ? 'Saving...' : 'Save'}
                        </button>
                </div>
        );

        const handleToggleOutOfOffice = () => {
                setSettings(prev => ({ ...prev, out_of_office: !prev.out_of_office }));
        };

        const handleToggleMonthlyReport = () => {
                setSettings(prev => ({ ...prev, monthly_report: !prev.monthly_report }));
        };

        const handleWeekdayToggle = (bit) => {
                setSettings(prev => ({ ...prev, email_weekdays: prev.email_weekdays ^ bit }));
        };

        const handleSiteSelection = (siteId) => {
                setSettings(prev => {
                        const isSelected = prev.subscribed_sites.includes(siteId);
                        if (isSelected) {
                                return {
                                        ...prev,
                                        subscribed_sites: prev.subscribed_sites.filter(id => id !== siteId),
                                };
                        } else {
                                return {
                                        ...prev,
                                        subscribed_sites: [...prev.subscribed_sites, siteId],
                                };
                        }
                });
        };

        // Save settings - always send all properties
        const handleSaveSettings = async () => {
                setSettingsSaving(true);
                try {
                        const payload = {
                                id: settings.id,
                                user_id: settings.user_id,
                                out_of_office: settings.out_of_office,
                                email_weekdays: settings.email_weekdays,
                                monthly_report: settings.monthly_report,
                                subscribed_sites: settings.subscribed_sites,
                        };
                        const response = await updateUserSettings(payload);
                        if (!response.ok) {
                                throw new Error('Failed to save settings');
                        }
                        showToast('success', 'Settings saved successfully');
                } catch (err) {
                        console.error('Failed to save settings:', err);
                        showToast('error', 'Failed to save settings');
                } finally {
                        setSettingsSaving(false);
                }
        };

        return (
                <div className="page-gradient min-h-screen">
                        <Header />
                        <main className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
                                <h1 className="text-3xl font-bold text-text-strong mb-8">Settings</h1>

                                {/* Account Information */}
                                <div className="mb-8 bg-white rounded-2xl shadow-card p-6 sm:p-8">
                                        <div className="flex items-center justify-between mb-6">
                                                <h2 className="text-xl font-bold text-text-strong">Account Information</h2>
                                                {!isEditingProfile && (
                                                        <button
                                                                onClick={handleEditProfile}
                                                                data-testid="profile-edit"
                                                                className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                                                                title="Edit profile"
                                                        >
                                                                <FontAwesomeIcon icon={faPencil} className="w-4 h-4" />
                                                        </button>
                                                )}
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                                                {/* Left column */}
                                                <div>
                                                        <span className="text-sm font-semibold text-text-weak">Name:</span>
                                                        {isEditingProfile ? (
                                                                <span className="ml-2 inline-flex gap-2">
                                                                        <input
                                                                                type="text"
                                                                                value={firstName}
                                                                                onChange={(e) => setFirstName(e.target.value)}
                                                                                className="w-28 px-2 py-1 border border-gray-300 rounded-lg text-text-strong focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                                                                                data-testid="profile-first-name"
                                                                                placeholder="First name"
                                                                        />
                                                                        <input
                                                                                type="text"
                                                                                value={lastName}
                                                                                onChange={(e) => setLastName(e.target.value)}
                                                                                className="w-28 px-2 py-1 border border-gray-300 rounded-lg text-text-strong focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                                                                                data-testid="profile-last-name"
                                                                                placeholder="Last name"
                                                                        />
                                                                </span>
                                                        ) : (
                                                                <span data-testid="profile-name" className="ml-2 text-text-strong">
                                                                        {[user?.firstName, user?.lastName].filter(Boolean).join(' ') || '-'}
                                                                </span>
                                                        )}
                                                </div>
                                                {/* Right column */}
                                                <div>
                                                        <span className="text-sm font-semibold text-text-weak">Organization:</span>
                                                        <span data-testid="profile-org" className="ml-2 text-text-strong">{user?.customerName || 'Loading...'}</span>
                                                </div>
                                                <div>
                                                        <span className="text-sm font-semibold text-text-weak">Email:</span>
                                                        <span data-testid="profile-email" className="ml-2 text-text-strong">{user?.email || 'Loading...'}</span>
                                                </div>
                                                <div>
                                                        <span className="text-sm font-semibold text-text-weak">Role:</span>
                                                        {user?.roleId && (
                                                                <span data-testid="profile-role" className={`ml-2 px-2 py-0.5 text-xs font-semibold rounded-full ${user.roleId === ROLE_SUPERADMIN ? 'bg-red-100 text-red-800' :
                                                                        user.roleId === ROLE_FACILITY_MANAGER ? 'bg-purple-100 text-purple-800' :
                                                                                'bg-green-100 text-green-800'
                                                                        }`}>
                                                                        {ROLE_NAMES[user.roleId]}
                                                                </span>
                                                        )}
                                                </div>
                                        </div>
                                        {isEditingProfile && (
                                                <div className="flex justify-end gap-2 mt-4">
                                                        <button
                                                                onClick={handleCancelEditProfile}
                                                                data-testid="profile-cancel"
                                                                disabled={profileSaving}
                                                                className="px-3 py-1 text-sm border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
                                                        >
                                                                Cancel
                                                        </button>
                                                        <button
                                                                onClick={handleSaveProfile}
                                                                data-testid="profile-save"
                                                                disabled={profileSaving}
                                                                className="px-3 py-1 text-sm bg-brand text-white rounded-lg hover:bg-brand/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                        >
                                                                {profileSaving ? 'Saving...' : 'Save'}
                                                        </button>
                                                </div>
                                        )}
                                </div>

                                {/* Location — a single-site customer never sees the site level on the
                                    dashboard, so this card is the only place they can name their
                                    location and set the country that tunes plant targets. Adding and
                                    renaming stay here at every site count, so neither control moves
                                    as a customer grows from one location to many; only the layout
                                    changes, from one labelled pair to a row per location. Deleting
                                    is left to the dashboard, which has the confirmation flow and the
                                    "site still has rooms" guard. */}
                                {sites.length > 0 && (
                                        <div className="mb-8 bg-white rounded-2xl shadow-card p-6 sm:p-8">
                                                <div className="flex items-center justify-between mb-2">
                                                        <h2 data-testid="locations-card" className="text-xl font-bold text-text-strong">{singleSite ? 'Location' : 'Locations'}</h2>
                                                        {editingSiteId === null && (
                                                                <div className="flex items-center gap-2">
                                                                        {singleSite && (
                                                                                <button
                                                                                        onClick={() => handleStartEditLocation(singleSite)}
                                                                                        data-testid="location-edit-single"
                                                                                        className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                                                                                        title="Edit location"
                                                                                >
                                                                                        <FontAwesomeIcon icon={faPencil} className="w-4 h-4" />
                                                                                </button>
                                                                        )}
                                                                        <AddSite
                                                                                noun="location"
                                                                                trigger="subtle"
                                                                                onSiteAdded={handleLocationAdded}
                                                                        />
                                                                </div>
                                                        )}
                                                </div>
                                                <p className="text-sm text-text-weak mb-6">
                                                        {singleSite
                                                                ? 'Your country adjusts the growing season used for plant health targets.'
                                                                : 'Each location has its own country, which adjusts the growing season used for plant health targets. Remove locations from the dashboard.'}
                                                </p>
                                                {!singleSite && (
                                                        <ul className="divide-y divide-gray-100">
                                                                {sites.map(site => (
                                                                        <li key={site.id} className="py-2">
                                                                                {editingSiteId === site.id ? (
                                                                                        <div className="flex flex-wrap items-center gap-2">
                                                                                                <input
                                                                                                        type="text"
                                                                                                        value={locationName}
                                                                                                        onChange={(e) => setLocationName(e.target.value)}
                                                                                                        className="w-40 px-2 py-1 border border-gray-300 rounded-lg text-text-strong focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                                                                                                        data-testid="location-name"
                                                                                                        placeholder="Location name"
                                                                                                />
                                                                                                <select
                                                                                                        data-testid="location-country"
                                                                                                        value={locationCountry}
                                                                                                        onChange={(e) => setLocationCountry(e.target.value)}
                                                                                                        className="px-2 py-1 border border-gray-300 rounded-lg text-text-strong focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                                                                                                >
                                                                                                        <option value="">No location</option>
                                                                                                        {SITE_COUNTRIES.map((c) => (
                                                                                                                <option key={c} value={c}>{c}</option>
                                                                                                        ))}
                                                                                                </select>
                                                                                                <div className="ml-auto">
                                                                                                        {locationEditActions}
                                                                                                </div>
                                                                                        </div>
                                                                                ) : (
                                                                                        <div className="flex items-center justify-between">
                                                                                                <span data-testid="location-row-name" className="text-text-strong">{site.name}</span>
                                                                                                <div className="flex items-center gap-2">
                                                                                                        <span data-testid="location-row-country" className="text-sm text-text-weak">{site.country || 'No country set'}</span>
                                                                                                        <button
                                                                                                                onClick={() => handleStartEditLocation(site)}
                                                                                                                data-testid="location-edit"
                                                                                                                data-site-id={site.id}
                                                                                                                className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                                                                                                                title={`Edit ${site.name}`}
                                                                                                        >
                                                                                                                <FontAwesomeIcon icon={faPencil} className="w-4 h-4" />
                                                                                                        </button>
                                                                                                </div>
                                                                                        </div>
                                                                                )}
                                                                        </li>
                                                                ))}
                                                        </ul>
                                                )}
                                                {singleSite && (
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                                                                <div>
                                                                        <span className="text-sm font-semibold text-text-weak">Name:</span>
                                                                        {isEditingSingleSite ? (
                                                                                <input
                                                                                        type="text"
                                                                                        value={locationName}
                                                                                        onChange={(e) => setLocationName(e.target.value)}
                                                                                        className="ml-2 w-40 px-2 py-1 border border-gray-300 rounded-lg text-text-strong focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                                                                                        data-testid="location-name-single"
                                                                                        placeholder="Location name"
                                                                                />
                                                                        ) : (
                                                                                <span data-testid="location-single-name" className="ml-2 text-text-strong">{singleSite.name}</span>
                                                                        )}
                                                                </div>
                                                                <div>
                                                                        <span className="text-sm font-semibold text-text-weak">Country:</span>
                                                                        {isEditingSingleSite ? (
                                                                                <select
                                                                                        value={locationCountry}
                                                                                        onChange={(e) => setLocationCountry(e.target.value)}
                                                                                        className="ml-2 px-2 py-1 border border-gray-300 rounded-lg text-text-strong focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                                                                                >
                                                                                        <option value="">No location</option>
                                                                                        {SITE_COUNTRIES.map((c) => (
                                                                                                <option key={c} value={c}>{c}</option>
                                                                                        ))}
                                                                                </select>
                                                                        ) : (
                                                                                <span className="ml-2 text-text-strong">{singleSite.country || 'Not set'}</span>
                                                                        )}
                                                                </div>
                                                        </div>
                                                )}
                                                {isEditingSingleSite && (
                                                        <div className="flex justify-end mt-4">
                                                                {locationEditActions}
                                                        </div>
                                                )}
                                        </div>
                                )}

                                {/* Grid layout for Change Password and Notifications */}
                                <div className={`grid grid-cols-1 ${user?.roleId === ROLE_PLANT_CARETAKER || user?.roleId === ROLE_FACILITY_MANAGER ? 'lg:grid-cols-2' : ''} gap-8`}>
                                        {/* Change Password Card */}
                                        <div className="bg-white rounded-2xl shadow-card p-6 sm:p-8">
                                                <h2 className="text-xl font-bold text-text-strong mb-6">Change Password</h2>

                                                <form onSubmit={handleChangePassword} className="space-y-4">
                                                        <div>
                                                                <label className="block text-sm font-semibold text-text-weak mb-2">
                                                                        Current Password
                                                                </label>
                                                                <input
                                                                        type="password"
                                                                        value={currentPassword}
                                                                        onChange={(e) => setCurrentPassword(e.target.value)}
                                                                        className="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                                                                        data-testid="password-current"
                                                                        placeholder="Enter current password"
                                                                />
                                                        </div>

                                                        <div>
                                                                <label className="block text-sm font-semibold text-text-weak mb-2">
                                                                        New Password
                                                                </label>
                                                                <input
                                                                        type="password"
                                                                        value={newPassword}
                                                                        onChange={(e) => setNewPassword(e.target.value)}
                                                                        className="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                                                                        data-testid="password-new"
                                                                        placeholder="Enter new password"
                                                                />
                                                        </div>

                                                        <div>
                                                                <label className="block text-sm font-semibold text-text-weak mb-2">
                                                                        Confirm New Password
                                                                </label>
                                                                <input
                                                                        type="password"
                                                                        value={confirmPassword}
                                                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                                                        className="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                                                                        data-testid="password-confirm"
                                                                        placeholder="Confirm new password"
                                                                />
                                                        </div>

                                                        {error && (
                                                                <p data-testid="password-error" className="text-red-500 text-sm">{error}</p>
                                                        )}

                                                        <button
                                                                type="submit"
                                                                data-testid="password-submit"
                                                                disabled={isSubmitting}
                                                                className="w-full py-3 bg-brand text-white font-semibold rounded-xl hover:bg-brand/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                        >
                                                                {isSubmitting ? 'Changing...' : 'Change Password'}
                                                        </button>
                                                </form>
                                        </div>

                                        {/* Email Notification Settings Card - Plant caretaker and Facility manager only */}
                                        {(user?.roleId === ROLE_PLANT_CARETAKER || user?.roleId === ROLE_FACILITY_MANAGER) && <div className="bg-white rounded-2xl shadow-card p-6 sm:p-8">
                                                <div className="flex items-center justify-between mb-4">
                                                        <h2 data-testid="notifications-card" className="text-xl font-bold text-text-strong">Email Notifications</h2>
                                                        <Tooltip align="right" content={user?.roleId === ROLE_FACILITY_MANAGER
                                                                ? (singleSite
                                                                        ? "You will receive an email on your selected weekdays with a summary of all your plants. Enabling Out of Office pauses all notifications."
                                                                        : "You will receive an email on your selected weekdays. The email includes a summary of all plants across all sites. Enabling Out of Office pauses all notifications.")
                                                                : (singleSite
                                                                        ? "You will receive an email on your selected weekdays with a summary of all your plants. Enabling Out of Office pauses all notifications."
                                                                        : "You will receive an email for each site you have selected on your selected weekdays. The email includes a summary of all plants on the site. Enabling Out of Office pauses all notifications.")
                                                        }>
                                                                <FontAwesomeIcon icon={faCircleQuestion} className="w-5 h-5 text-gray-400 hover:text-gray-600 cursor-default" />
                                                        </Tooltip>
                                                </div>
                                                <p className="text-sm text-text-weak mb-6">
                                                        Manage your email notification preferences
                                                </p>

                                                {settingsLoading ? (
                                                        <div className="flex justify-center items-center py-8">
                                                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
                                                        </div>
                                                ) : (
                                                        <div className="space-y-4">
                                                                {/* Out of Office Toggle */}
                                                                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                                                                        <div>
                                                                                <h3 className="font-semibold text-text-strong">Out of Office</h3>
                                                                                <p className="text-sm text-text-weak">Pause all email notifications temporarily</p>
                                                                        </div>
                                                                        <button
                                                                                onClick={handleToggleOutOfOffice}
                                                                                data-testid="ooo-toggle"
                                                                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings.out_of_office ? 'bg-brand' : 'bg-gray-300'}`}
                                                                        >
                                                                                <span
                                                                                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings.out_of_office ? 'translate-x-6' : 'translate-x-1'}`}
                                                                                />
                                                                        </button>
                                                                </div>

                                                                {/* Email Days */}
                                                                {!settings.out_of_office && (
                                                                        <div className="py-3 border-b border-gray-100">
                                                                                <h3 className="font-semibold text-text-strong mb-1">Email Days</h3>
                                                                                <p className="text-sm text-text-weak mb-3">Select which weekdays to receive your plant health status.</p>
                                                                                <div className="flex gap-2">
                                                                                        {WEEKDAYS.map(({ label, bit }) => (
                                                                                                <button
                                                                                                        key={bit}
                                                                                                        type="button"
                                                                                                        onClick={() => handleWeekdayToggle(bit)}
                                                                                                        data-testid="weekday"
                                                                                                        data-weekday={label}
                                                                                                        data-weekday-bit={bit}
                                                                                                        data-selected={(settings.email_weekdays & bit) ? 'true' : 'false'}
                                                                                                        className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${settings.email_weekdays & bit
                                                                                                                ? 'bg-brand text-white'
                                                                                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                                                                                }`}
                                                                                                >
                                                                                                        {label}
                                                                                                </button>
                                                                                        ))}
                                                                                </div>
                                                                        </div>
                                                                )}

                                                                {/* Monthly Report - Facility manager only */}
                                                                {!settings.out_of_office && user?.roleId === ROLE_FACILITY_MANAGER && (
                                                                        <div className="flex items-center justify-between py-3 border-b border-gray-100">
                                                                                <div>
                                                                                        <h3 className="font-semibold text-text-strong">Monthly Report</h3>
                                                                                        <p className="text-sm text-text-weak">Receive a PDF overview of all your plants on the 1st of each month</p>
                                                                                </div>
                                                                                <button
                                                                                        type="button"
                                                                                        onClick={handleToggleMonthlyReport}
                                                                                        data-testid="monthly-toggle"
                                                                                        data-selected={settings.monthly_report ? 'true' : 'false'}
                                                                                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings.monthly_report ? 'bg-brand' : 'bg-gray-300'}`}
                                                                                >
                                                                                        <span
                                                                                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings.monthly_report ? 'translate-x-6' : 'translate-x-1'}`}
                                                                                        />
                                                                                </button>
                                                                        </div>
                                                                )}

                                                                {/* Site Subscriptions */}
                                                                {!settings.out_of_office && settings.email_weekdays > 0 && user?.roleId !== ROLE_FACILITY_MANAGER && (
                                                                        <div className="py-3">
                                                                                {/* With one site there is nothing to pick between, so this reads
                                                                                    as a plain on/off for the report rather than a site subscription. */}
                                                                                <h3 data-testid="subscribed-sites" className="font-semibold text-text-strong">{singleSite ? 'Health Report' : 'Subscribed Sites'}</h3>
                                                                                <p className="text-sm text-text-weak mb-2">
                                                                                        {singleSite ? 'Receive the plant health report by email.' : 'Select sites to include in the report.'}
                                                                                </p>
                                                                                <div className="space-y-2">
                                                                                        {sites.map(site => (
                                                                                                <div key={site.id} className="flex items-center">
                                                                                                        <input
                                                                                                                type="checkbox"
                                                                                                                id={`site-${site.id}`}
                                                                                                                data-testid="site-subscription"
                                                                                                                data-site-id={site.id}
                                                                                                                checked={settings.subscribed_sites.includes(site.id)}
                                                                                                                onChange={() => handleSiteSelection(site.id)}
                                                                                                                className="h-4 w-4 text-brand border-gray-300 rounded focus:ring-brand"
                                                                                                        />
                                                                                                        <label htmlFor={`site-${site.id}`} className="ml-2 text-sm text-text-strong">
                                                                                                                {singleSite ? 'Send me the report' : site.name}
                                                                                                        </label>
                                                                                                </div>
                                                                                        ))}
                                                                                </div>
                                                                        </div>
                                                                )}

                                                                {/* Save Button */}
                                                                <button
                                                                        onClick={handleSaveSettings}
                                                                        data-testid="notifications-save"
                                                                        disabled={settingsSaving}
                                                                        className="w-full mt-6 py-3 bg-brand text-white font-semibold rounded-xl hover:bg-brand/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                                >
                                                                        {settingsSaving ? 'Saving...' : 'Save Settings'}
                                                                </button>
                                                        </div>
                                                )}
                                        </div>}
                                </div>
                        </main>
                        <Footer />
                </div>
        );
};

export default SettingsPage;
